import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Users, Clock, CalendarRange,
  Briefcase, Sparkles, Package, MapPin, Megaphone, FileText,
  CreditCard, Settings as SettingsIcon, LogOut, Inbox, Repeat, Gift as GiftIcon, Globe, Tag, ClipboardList, MessageSquare,
  BadgePercent,
} from 'lucide-react';
import axios from 'axios';
import { AuthContext } from '../../AuthContext';
import useAssistantAvailable from '../../assistant/useAssistantAvailable';
import usePriceBoardEnabled from '../../prices/usePriceBoardEnabled';
import NotificationsBell from '../NotificationsBell';

// Provider desktop chrome — fixed 256px left sidebar + sticky topbar.
// Renders only at lg+ (hidden lg:flex). On mobile, the existing
// Navigation/Header.js continues to handle chrome. Wraps each provider
// route at the React Router level (App.js) via:
//   <ProviderShell active="dashboard"><ProviderDashboard /></ProviderShell>
//
// The active prop is a stable section key matching NAV_GROUPS entries,
// not the URL pathname — keeps the highlight stable across query
// params, sub-routes, and minor URL drift.

const NAV_GROUPS = [
  {
    label: null, // no group label for the primary trio
    items: [
      { key: 'dashboard',    href: '/provider',                label: 'Dashboard',     icon: LayoutDashboard },
      { key: 'appointments', href: '/provider/appointments',   label: 'Appointments',  icon: Calendar },
      { key: 'requests',     href: '/provider/requests',       label: 'Requests',      icon: Inbox, badgeKey: 'pendingRequests' },
      { key: 'clients',      href: '/provider/roster',         label: 'Roster',        icon: Users },
      { key: 'notes',        href: '/provider/notes',          label: 'SOAP notes',    icon: ClipboardList },
      // Shown only when the assistant is on for this practice (useAssistantAvailable).
      { key: 'assistant',    href: '/provider/assistant',      label: 'Assistant',     icon: MessageSquare },
    ],
  },
  {
    label: 'Schedule',
    items: [
      { key: 'availability',  href: '/provider/availability',       label: 'Availability', icon: Clock },
      { key: 'weekly-hours',  href: '/provider/schedule-template',  label: 'Weekly hours', icon: CalendarRange },
      { key: 'events',        href: '/provider/events',             label: 'Events',       icon: Briefcase },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { key: 'services',  href: '/provider/services',  label: 'Services',  icon: Sparkles },
      // The price board replaces Services where it's on (usePriceBoardEnabled).
      { key: 'prices',    href: '/provider/prices',    label: 'Prices',    icon: Tag },
      { key: 'packages',  href: '/provider/packages',  label: 'Packages',  icon: Package },
      { key: 'memberships', href: '/provider/memberships', label: 'Memberships', icon: Repeat },
      { key: 'gifts',     href: '/provider/gifts',     label: 'Gifts',     icon: GiftIcon },
      { key: 'locations', href: '/provider/locations', label: 'Locations', icon: MapPin },
    ],
  },
  {
    label: 'Business',
    items: [
      { key: 'outreach', href: '/provider/outreach', label: 'Outreach', icon: Megaphone },
      { key: 'promotions', href: '/provider/promotions', label: 'Promotions', icon: BadgePercent },
      { key: 'reports',  href: '/provider/reports',  label: 'Reports',  icon: FileText },
      { key: 'billing',  href: '/provider/billing',  label: 'Billing',  icon: CreditCard },
    ],
  },
];

const ProviderShell = ({ active, children }) => {
  const { user, setUser } = useContext(AuthContext);
  const assistantOn = useAssistantAvailable(user);
  const pricesOn = usePriceBoardEnabled(user) === true;
  const shownGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => (it.key !== 'assistant' || assistantOn)
      && (it.key !== 'prices' || pricesOn) && (it.key !== 'services' || !pricesOn)),
  }));
  // "My public page" → /p/:joinCode (the landing page + its editor).
  // Injected at render — the static NAV_GROUPS can't carry a per-user path.
  const navGroups = user?.joinCode
    ? shownGroups.map((g, i) => (i === 0
        ? { ...g, items: [...g.items, { key: 'my-page', href: `/p/${user.joinCode}`, label: 'My public page', icon: Globe }] }
        : g))
    : shownGroups;

  // Pending request count — drives the Inbox nav-item badge. Sums BOTH
  // queues the Requests page shows: pending Bookings (publicBookingMode
  // 'on') AND pending ContactRequests (publicBookingMode 'approval').
  // Counting only bookings silently under-badges approval-only providers.
  // Polls every 60s. Keep noise low; an off-by-one for a minute is
  // fine since the Requests page itself re-fetches authoritatively.
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (user?.accountType !== 'PROVIDER') return;
    let cancelled = false;
    const fetchCount = async () => {
      // allSettled so one failing endpoint doesn't zero out the badge;
      // each count contributes independently.
      const [bookings, contacts, joins] = await Promise.allSettled([
        axios.get('/api/bookings/requests/pending', { withCredentials: true }),
        axios.get('/api/contact-requests/pending', { withCredentials: true }),
        // Third queue: join requests (clientJoinMode 'approval').
        axios.get('/api/provider-requests/pending', { withCredentials: true }),
      ]);
      if (cancelled) return;
      const bookingCount = bookings.status === 'fulfilled' ? (bookings.value.data?.count || 0) : 0;
      const contactCount = contacts.status === 'fulfilled' ? (contacts.value.data?.count || 0) : 0;
      const joinCount = joins.status === 'fulfilled' ? (joins.value.data?.requests?.length || 0) : 0;
      setPendingCount(bookingCount + contactCount + joinCount);
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.accountType]);

  const badgeValues = { pendingRequests: pendingCount };

  // Mirror Header.js's logout flow exactly so behavior is identical
  // whether the provider hits logout from mobile or desktop:
  //   POST /api/auth/logout → clear AuthContext → window.replace('/login').
  // Errors get logged; no toast since the provider is on their way out
  // anyway and the next /login page will surface the auth state.
  const handleLogout = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        setUser(null);
        window.location.replace('/login');
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Avatar initials — fall back to the email's first letter if no full
  // name is on file.
  const initials = (() => {
    const name = user?.profile?.fullName || user?.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0]?.[0] || '?').toUpperCase();
  })();

  const subscriptionPlanLabel = (() => {
    if (user?.providerProfile?.subscriptionGrandfathered) return 'Provider · Complimentary';
    return 'Provider · $39.99/mo';
  })();

  return (
    <>
      {/* Fixed left sidebar — desktop only. On <lg the existing
          Navigation/Header renders the mobile nav. */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-paper-elev border-r border-line flex-col z-30">
        {/* Brand — real Avayble logo asset, no sub-label. */}
        <Link to="/provider" className="flex items-center px-5 pt-5 pb-5">
          <img src="/imgs/avayble_logo.png" alt="Avayble" className="h-10 w-auto" />
        </Link>

        {/* Scrollable nav. Hide scrollbar but keep it scrollable. */}
        <nav className="flex-1 overflow-y-auto px-3 pt-1 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {navGroups.map((group, gi) => (
            <div key={gi} className={gi === 0 ? '' : 'mt-4'}>
              {group.label && (
                <div className="font-mono text-[9.5px] text-ink-3 tracking-[0.18em] uppercase px-2.5 pb-1.5 opacity-85">
                  {group.label}
                </div>
              )}
              {group.items.map(item => {
                const Icon = item.icon;
                const isActive = active === item.key;
                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    className={`relative flex items-center gap-3 px-2.5 py-2 rounded-btn text-sm transition-colors ${
                      isActive
                        ? 'bg-accent-soft text-accent-ink font-semibold'
                        : 'text-ink-2 hover:bg-paper-deep hover:text-ink font-medium'
                    }`}
                  >
                    {isActive && (
                      <span
                        className="absolute -left-3 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-accent rounded-r"
                        aria-hidden="true"
                      />
                    )}
                    <Icon className="w-[17px] h-[17px] flex-shrink-0" strokeWidth={1.75} />
                    <span className="flex-1">{item.label}</span>
                    {item.badgeKey && badgeValues[item.badgeKey] > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent text-white text-[10px] font-semibold leading-none">
                        {badgeValues[item.badgeKey]}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-line px-4 py-3.5 flex items-center gap-3">
          <div className="w-[38px] h-[38px] rounded-full bg-paper-deep border border-line grid place-items-center font-display font-semibold text-[1.05rem] text-accent-ink flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-ink truncate">
              {user?.profile?.fullName || user?.email}
            </div>
            <div className="text-[0.72rem] text-ink-3 truncate">{subscriptionPlanLabel}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <NotificationsBell className="w-8 h-8 !p-0 grid place-items-center border border-line text-ink-2 hover:bg-paper-deep hover:text-ink" />
            <Link
              to="/provider/settings"
              aria-label="Settings"
              className="w-8 h-8 rounded-btn border border-line text-ink-2 hover:bg-paper-deep hover:text-ink grid place-items-center transition-colors"
            >
              <SettingsIcon className="w-4 h-4" strokeWidth={1.75} />
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Log out"
              title="Log out"
              className="w-8 h-8 rounded-btn border border-line grid place-items-center transition-colors"
              style={{ color: 'var(--r-off-ink)' }}
              onMouseOver={(e) => (e.currentTarget.style.background = 'var(--r-off-soft)')}
              onMouseOut={(e) => (e.currentTarget.style.background = '')}
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main column — offset by the sidebar width on desktop. No
          topbar — search/date/book-session were all removed per spec.
          Page-level CTAs live inside each page's own header. */}
      <div className="lg:ml-64 lg:min-h-screen lg:flex lg:flex-col">
        {/* Content. Desktop gets the wide centered max-w-[1480px] frame
            with shell padding. Mobile renders children raw — they
            already have their own page-level containers. */}
        <main className="lg:flex-1 lg:px-9 lg:pt-8 lg:pb-16 lg:mx-auto lg:max-w-[1480px] w-full">
          {children}
        </main>
      </div>
    </>
  );
};

// Mobile fallback — at <lg, defer to existing Header chrome and render
// children full-width. Used by routes that want the shell on desktop
// but the legacy navigation on mobile.
export const ProviderShellMobile = ({ children }) => (
  <div className="lg:hidden">{children}</div>
);

export default ProviderShell;
